import { useMemo, useState, useEffect } from "react";
import dayjs from "dayjs";
import { setCookie } from "../utils/cookie";
import { isForkPage, redirectToUrl } from "../utils/url/urlUtils";
import { IconButton, styled } from "@mui/material";
import { Icon } from "../common/components/icon/Icon";
import { IconType } from "../common/components/icon/IconType";
import { SimpleSelect } from "../common/components/Select/SimpleSelect";
import { resolveApiUrl } from "../utils/url/urlResolver";
import { useDispatch, useSelector } from "react-redux";
import { updateVisibility } from "./actions";
import { selectRegionaBannerVisibility } from "./selectors";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import GlobalBanner from "~/Header/components/TopBannersSection/GlobalBanner/GlobalBanner";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { getRegionCookie } from "~/ForkPage/regionCookie";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

interface Country {
  isocode: string;
  name: string;
}

const dropdownOptions = window.SCHA.regionSelectionBanner.dropdownUrls;
const defaultDropdownOption = window.SCHA.regionSelectionBanner.dropdownUrls[0];

export const RegionSelectionBanner = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isOpen = useSelector(selectRegionaBannerVisibility);
  const [dropdownOption, setDropdownOption] = useState(defaultDropdownOption);

  const isTabOrDesktop = useIsMinTabletSize();

  const handleConfirmClick = () => {
    dispatch(updateVisibility(false));
    if (dropdownOption.value == "1") {
      redirectToUrl(dropdownOption.url);
    } else {
      fetchRegionSiteAndRedirect(dropdownOption.url);
    }
  };

  const handleCloseClick = () => {
    dispatch(updateVisibility(false));
    setCookie({
      name: COOKIE,
      value: "closed",
      expireDate: dayjs().add(1, "w").toDate(), // expires in 1 week
    });
  };

  const options = useMemo(() => {
    return dropdownOptions.map((option) => {
      return {
        ...option,
        label: t(option.label),
      };
    });
  }, [t]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="scha__region-selection-banner__wrapper scha__screen-only">
      <div className="scha__region-selection-banner container">
        <div className={"scha__region-selection-banner__message"}>{t("regionSelectionBanner.message")}</div>

        <div className={"scha__region-selection-banner__select"}>
          <SimpleSelect
            value={dropdownOption?.value}
            values={options}
            onChange={(e) => setDropdownOption(dropdownOptions[+e.target.value])}
            label={t("regionSelectionBanner.region")}
            id={"region_selection_dropdown"}
            fullWidth
          />
        </div>
        <div className="scha__region-selection-banner__actions">
          {isTabOrDesktop ? (
            <Button
              size={"medium"}
              className={"scha__region-selection-banner__confirm-button"}
              onClick={handleConfirmClick}
            >
              {t("regionSelectionBanner.apply")}
            </Button>
          ) : (
            <IconButton
              variant="contained"
              color={"primary"}
              className={"scha__region-selection-banner__confirm-button"}
              onClick={handleConfirmClick}
            >
              <ArrowForwardIcon />
            </IconButton>
          )}

          <div className={"scha__region-selection-banner__close-button"}>
            <IconButton color={"primary"} onClick={handleCloseClick}>
              <Icon type={IconType.Close} />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

const COOKIE = "cache.region";
const paramsKey = "redirectedCountry";

export const showRegionBanner = () => {
  return window.SCHA.regionSelectionBanner.showRedirectBanner && getRegionCookie() !== "closed" && !isForkPage();
};

export function fetchRegionSiteAndRedirect(countryIsoCode: string) {
  let uri = window.location.href.replace(window.location.origin, "");
  if (isForkPage()) {
    uri = uri.replace("/static/welcome", "");
  }
  const params = new URLSearchParams();
  params.set("countryIsoCode", countryIsoCode);
  params.set("currentUri", uri);

  const url = resolveApiUrl(`/regionSelectionBanner/getRegionSite?${params}`);
  fetch(url, {
    method: "GET",
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    headers: {
      dataType: "json",
      "content-type": "application/json",
    },
  }).then((response) => {
    if (!response.ok) {
      throw alert("Error status: " + response.status + ", please try again");
    } else {
      response.json().then((data) => {
        const parsedData = JSON.parse(data);

        const url = new URL(parsedData.url);
        if (parsedData.isRedirectedCountry) {
          url.searchParams.set(paramsKey, countryIsoCode);
        }
        redirectToUrl(url.toString());
      });
    }
  });
}

export const RedirectionTopBanner = () => {
  const { t } = useTranslation();

  const [countryInfo, setCountryInfo] = useState<Country | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const country = urlParams.get(paramsKey);

    if (country && country.length > 0 && !isForkPage()) {
      const info = window.SCHA?.countryOptions?.find((item: Country) => item.isocode === country);
      setCountryInfo(info || null);
    }
  }, []);

  const handleCloseClick = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(paramsKey);
    window.history.replaceState({}, "", url.toString());
    setCountryInfo(null);
  };

  const redirectionMessage = (
    <GlobalBanner type="secondary">
      <BannerContent className="container">
        <BannerMessage>
          {t("regionSelectionBanner.redirection.message")}
          {countryInfo && <strong>{` ${t(countryInfo.name)}.`}</strong>}
        </BannerMessage>
        <BannerAction>
          <IconButton color="primary" onClick={handleCloseClick}>
            <Icon type={IconType.Close} />
          </IconButton>
        </BannerAction>
      </BannerContent>
    </GlobalBanner>
  );

  return (
    <div className="scha__root-region-banner">
      {countryInfo && redirectionMessage}
      {showRegionBanner() ? <RegionSelectionBanner /> : null}
    </div>
  );
};

export function createRegionSelectionBanner() {
  return <RedirectionTopBanner />;
}

const BannerContent = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const BannerAction = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginRight: "-10px",

  [theme.breakpoints.down("tablet")]: {
    position: "absolute",
    top: "10px",
    right: "10px",
  },
}));

const BannerMessage = styled("div")(({ theme }) => ({
  ...getFontStyle(FontSize.TitleMedium),
  marginRight: "30px",

  [theme.breakpoints.down("tablet")]: {
    padding: "12px 0",
  },
}));
